import type { Channel, Client, User } from "stoat.js";

/**
 * Resolve the primary caller for an incoming or waiting call in a channel.
 */
export function getIncomingCaller(
  client: Client,
  channel: Channel,
): User | undefined {
  const selfId = client.user?.id;
  if (!selfId) return undefined;

  if (channel.type === "DirectMessage") {
    return channel.recipient ?? undefined;
  }

  for (const participant of channel.voiceParticipants.values()) {
    if (participant.userId !== selfId) {
      return client.users.get(participant.userId);
    }
  }

  return undefined;
}

/**
 * Find a voice channel the given user is in that the local user can join.
 */
export function findJoinableVoiceChannel(
  client: Client,
  userId: string,
): Channel | undefined {
  const selfId = client.user?.id;
  if (!selfId || userId === selfId) return undefined;

  for (const channel of client.channels.values()) {
    if (!channel.voiceParticipants.has(userId)) continue;
    if (channel.voiceParticipants.has(selfId)) continue;

    if (channel.type === "DirectMessage") {
      if (channel.recipientIds.has(userId)) return channel;
      continue;
    }

    if (channel.type === "Group") {
      if (channel.recipientIds.has(selfId) && channel.recipientIds.has(userId)) {
        return channel;
      }
      continue;
    }

    if (channel.isVoice && channel.havePermission("Connect")) {
      return channel;
    }
  }

  return undefined;
}

/**
 * Resolve a channel for an incoming call invitation.
 */
export function resolveIncomingCallChannel(client: Client): Channel | undefined {
  const selfId = client.user?.id;
  if (!selfId) return undefined;

  for (const channel of client.channels.values()) {
    if (channel.voiceParticipants.size === 0) continue;
    if (channel.voiceParticipants.has(selfId)) continue;

    if (channel.type === "DirectMessage" || channel.type === "Group") {
      return channel;
    }
  }

  return undefined;
}
